import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { SocialComment, SocialPost } from "../types/socialPost";

const PAGE_SIZE = 20;

export interface GlobalFeedEntry {
  id: string;
  authorId: string;
  postId: string;
  postTimestamp: Timestamp | null;
  post: SocialPost | null;
}

export async function hydrateSocialPost(
  authorId: string,
  postId: string,
): Promise<SocialPost | null> {
  const postSnap = await getDoc(
    doc(db, "posts", authorId, "postsList", postId),
  );
  if (!postSnap.exists()) return null;

  const data = postSnap.data();

  const [likesSnapshot, commentsSnapshot] = await Promise.all([
    getDocs(collection(db, "postLikes", authorId, "postsList", postId, "likes")),
    getDocs(
      collection(db, "postComments", authorId, "postsList", postId, "comments"),
    ),
  ]);

  const likesList = likesSnapshot.docs.map((d) => d.id);
  const CommentsList: SocialComment[] = commentsSnapshot.docs
    .map((d) => ({
      commentId: d.id,
      userId: d.data().userId as string,
      text: (d.data().text as string) || "",
      timestamp: (d.data().timestamp as Timestamp) || null,
    }))
    .sort((a, b) => {
      const aMs = a.timestamp?.toMillis?.() ?? 0;
      const bMs = b.timestamp?.toMillis?.() ?? 0;
      return aMs - bMs;
    });

  return {
    id: postSnap.id,
    userID: (data.userID as string) || authorId,
    username: (data.username as string) || "",
    userImageUrl: (data.userImageUrl as string) || "",
    postTimestamp: (data.postTimestamp as Timestamp) || null,
    notes: typeof data.notes === "string" ? data.notes : "",
    duration: typeof data.duration === "number" ? data.duration : 0,
    mediaList: Array.isArray(data.mediaList) ? data.mediaList : [],
    load: typeof data.load === "number" ? data.load : undefined,
    AverageHR: typeof data.AverageHR === "number" ? data.AverageHR : undefined,
    trainingLogID:
      typeof data.trainingLogID === "string" ? data.trainingLogID : undefined,
    activityLogID:
      typeof data.activityLogID === "string" ? data.activityLogID : undefined,
    performanceLogID:
      typeof data.performanceLogID === "string"
        ? data.performanceLogID
        : undefined,
    sessionID: typeof data.sessionID === "string" ? data.sessionID : undefined,
    isPrivate: data.isPrivate === true,
    gpsDataIsPrivate: data.gpsDataIsPrivate === true,
    likesList,
    CommentsList,
  };
}

/**
 * Fetch a page of the app-wide global community feed (`globalFeed`), newest first.
 * Hydrates each entry with the full source post (including likes/comments).
 */
export const getGlobalFeedPage = async (
  lastPostTimestamp?: Date,
): Promise<GlobalFeedEntry[]> => {
  const feedCollection = collection(db, "globalFeed");
  const feedQuery = lastPostTimestamp
    ? query(
        feedCollection,
        orderBy("postTimestamp", "desc"),
        where("postTimestamp", "<", Timestamp.fromDate(lastPostTimestamp)),
        limit(PAGE_SIZE),
      )
    : query(feedCollection, orderBy("postTimestamp", "desc"), limit(PAGE_SIZE));

  const snap = await getDocs(feedQuery);

  return Promise.all(
    snap.docs.map(async (feedDoc) => {
      const data = feedDoc.data();
      const authorId = (data.authorId as string) || "";
      const postId = (data.postId as string) || "";
      let post: SocialPost | null = null;

      if (authorId && postId) {
        try {
          post = await hydrateSocialPost(authorId, postId);
        } catch (err) {
          console.error(`Failed to hydrate post ${authorId}-${postId}:`, err);
        }
      }

      return {
        id: feedDoc.id,
        authorId,
        postId,
        postTimestamp: (data.postTimestamp as Timestamp) || post?.postTimestamp || null,
        post,
      } satisfies GlobalFeedEntry;
    }),
  );
};

export const GLOBAL_FEED_PAGE_SIZE = PAGE_SIZE;
